import type { JSX } from 'react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RestrictedField } from '@/features/guardian/restricted-field';
import { useEvaluations } from '@/features/memorization/hooks';
import { EVALUATION_OUTCOME_LABELS, RUBRIC_SCORE_LABELS } from '@/features/memorization/labels';
import type { SegmentView } from '@/features/memorization/types';

const PAGE_SIZE = 20;

function ScoreRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <RestrictedField
        value={value}
        render={(score) => (
          <span>{RUBRIC_SCORE_LABELS[score as keyof typeof RUBRIC_SCORE_LABELS]}</span>
        )}
      />
    </div>
  );
}

function SegmentRow({ segment }: { readonly segment: SegmentView }): JSX.Element {
  // scoreAccuracy is REQUIRED at write time (never optional in
  // evaluateSubmissionSchema/createCorrectionSchema) - a null value here
  // can only mean the backend's LIMITED_READ redaction ran
  // (redactSegmentForLimitedRead nulls all four scores and notes
  // together). notes, by contrast, is genuinely optional at write time,
  // so its own null is ambiguous by itself; this segment-level redaction
  // signal is what makes it safe to route notes through RestrictedField
  // only when redaction is actually known to apply, never when a teacher
  // simply left notes blank.
  const isRedacted = segment.scoreAccuracy === null;

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="text-sm font-medium">
          Ayat {segment.startAyah}
          {segment.endAyah !== segment.startAyah ? `-${segment.endAyah}` : ''}
          {segment.submissionId === null && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">(Koreksi)</span>
          )}
        </span>
        <Badge variant={segment.outcome === 'ACCEPTED' ? 'default' : 'destructive'}>
          {EVALUATION_OUTCOME_LABELS[segment.outcome]}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <ScoreRow label="Akurasi" value={segment.scoreAccuracy} />
        <ScoreRow label="Kelancaran" value={segment.scoreFluency} />
        <ScoreRow label="Tajwid" value={segment.scoreTajwid} />
        <ScoreRow label="Kepercayaan Diri" value={segment.scoreConfidence} />
      </div>
      {isRedacted ? (
        <RestrictedField value={null} render={() => null} className="text-xs" />
      ) : (
        segment.notes !== null && (
          <p className="text-xs text-muted-foreground">Catatan: {segment.notes}</p>
        )
      )}
      <span className="text-xs text-muted-foreground">
        {new Date(segment.evaluatedAt).toLocaleString('id-ID')}
      </span>
    </li>
  );
}

/**
 * Evaluation read view (Phase G-2, Job 14). LIMITED_READ redaction is the
 * backend's job (ADR-025's Addendum) - this component only ever renders
 * whatever the API actually returned, via `RestrictedField` for the
 * fields that policy may have already nulled. It never attempts to
 * reconstruct a redacted value and never renders a restricted score as a
 * bare `0` or `-`.
 */
export function EvaluationHistory({
  targetId,
  submissionId,
}: {
  readonly targetId: string;
  readonly submissionId?: string;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useEvaluations(targetId, page, PAGE_SIZE, submissionId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-destructive">Gagal memuat riwayat evaluasi.</p>;
  }

  if (data === undefined || data.items.length === 0) {
    return <p className="text-sm text-muted-foreground">Belum ada evaluasi.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {data.items.map((segment) => (
          <SegmentRow key={segment.id} segment={segment} />
        ))}
      </ul>

      {data.total_pages > 1 && (
        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => {
              setPage((current) => Math.max(1, current - 1));
            }}
          >
            Sebelumnya
          </Button>
          <span className="text-xs text-muted-foreground">
            Halaman {data.page} dari {data.total_pages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= data.total_pages}
            onClick={() => {
              setPage((current) => Math.min(data.total_pages, current + 1));
            }}
          >
            Berikutnya
          </Button>
        </div>
      )}
    </div>
  );
}
